import { useEffect, useRef, useState } from 'react';
import { RoccoLink } from '@rocco/components/link';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { useHeroMarket } from './hero-markets';

/**
 * Homepage hero (BC-4479).
 *
 * A static, per-market project banner (`useHeroMarket`) under a large headline,
 * with a subtle parallax as the page scrolls and a hyperlinked "Featured
 * Project" credit in the bottom corner. The banner is not locked to the
 * viewport — it keeps the Figma frame's natural height (1920×1373 ⇒ 71.51vw),
 * so the tagline/CTA lower in the hero and the intro section beneath reveal as
 * you scroll. Replaces the previous full-bleed hero video.
 */
const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });
    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(query.matches);
        const onChange = () => setReduced(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    return reduced;
};

/**
 * Subtle background parallax tied to the hero's scroll position. Writes the
 * transform straight onto the banner image in a rAF-throttled scroll handler —
 * no React state, so the page doesn't re-render on every frame (which made the
 * effect stutter). The banner is rendered 12% taller than the section, so the
 * shift is clamped to that slack to avoid revealing an edge; disabled entirely
 * under reduced motion.
 */
const useHeroParallax = (
    containerRef: React.RefObject<HTMLElement>,
    targetRef: React.RefObject<HTMLElement>,
    enabled: boolean,
) => {
    useEffect(() => {
        const container = containerRef.current;
        const target = targetRef.current;
        if (!container || !target) return;
        if (!enabled) {
            target.style.transform = '';
            return;
        }

        let raf = 0;
        const update = () => {
            raf = 0;
            const rect = container.getBoundingClientRect();
            const maxShift = rect.height * 0.06;
            const next = Math.min(Math.max(-rect.top * 0.18, 0), maxShift);
            target.style.transform = `translate3d(0, ${next.toFixed(1)}px, 0)`;
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [containerRef, targetRef, enabled]);
};

/**
 * The headline is specced as Helvetica Neue Light in Figma — not the brand
 * `font-sans` (Neue Haas Grotesk), which renders noticeably heavier. Apply the
 * Helvetica Neue stack directly so it matches the design.
 */
const HEADLINE_FONT =
    'neue-haas-grotesk-text, "Helvetica Neue", Helvetica, Arial, sans-serif';

/** The hero headline, split into two lines for the masked line reveal. */
const HEADLINE_LINES = ['Where beautifully', 'designed spaces begin'] as const;

type HeroHeadlineAnimationState = 'idle' | 'pending' | 'played';

/**
 * Keeps the hero reveal as a one-shot animation for each browser runtime. This
 * avoids replaying the entry motion if the hero subtree re-mounts during
 * initial hydration.
 */
let heroHeadlineAnimationState: HeroHeadlineAnimationState = 'idle';

const reserveHeroHeadlineAnimation = () => {
    if (heroHeadlineAnimationState !== 'idle') {
        return false;
    }
    heroHeadlineAnimationState = 'pending';
    return true;
};

const commitHeroHeadlineAnimation = () => {
    heroHeadlineAnimationState = 'played';
};

const releaseHeroHeadlineAnimationReservation = () => {
    if (heroHeadlineAnimationState === 'pending') {
        heroHeadlineAnimationState = 'idle';
    }
};

/** Resets the one-shot state between tests. */
export const __resetHeroHeadlineAnimationStateForTests = () => {
    heroHeadlineAnimationState = 'idle';
};

/**
 * Plays the hero reveal only for the first client mount, and keeps it static
 * afterwards so the heading does not replay during hydration remounts.
 */
const useHeroHeadlineRevealAnimation = (reducedMotion: boolean) => {
    const [shouldAnimate, setShouldAnimate] = useState(false);

    useEffect(() => {
        if (reducedMotion) {
            return;
        }
        if (!reserveHeroHeadlineAnimation()) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            commitHeroHeadlineAnimation();
            setShouldAnimate(true);
        });

        return () => {
            window.cancelAnimationFrame(frameId);
            releaseHeroHeadlineAnimationReservation();
        };
    }, [reducedMotion]);

    return shouldAnimate;
};

/**
 * Headline with an Exo-Ape-style line reveal: each line sits in an
 * overflow-hidden mask and slides up from below, the second line a beat after
 * the first. Pure transform under a mask (no opacity) for a crisp wipe. Honours
 * `prefers-reduced-motion` by rendering the lines in place with no movement.
 */
const RevealHeadline = ({
    className,
    style,
    animate,
}: {
    className: string;
    style?: React.CSSProperties;
    animate: boolean;
}) => (
    <h1 className={className} style={style}>
        {HEADLINE_LINES.map((line, index) => (
            <span
                key={line}
                className="-mb-[0.12em] block overflow-hidden pb-[0.12em]"
            >
                <span
                    className={`${animate ? 'hero-line-rise ' : ''}block ${animate ? 'will-change-transform' : ''}`}
                    style={{ animationDelay: `${index * 120}ms` }}
                >
                    {line}
                </span>
            </span>
        ))}
    </h1>
);

export const HeroSection = () => {
    const { handleCTAClick, handleLoginClick } = useCTAHandlers('HomePageHero');
    const { image, featuredProject } = useHeroMarket();

    const reducedMotion = usePrefersReducedMotion();
    const shouldAnimateHeadline = useHeroHeadlineRevealAnimation(reducedMotion);
    const sectionRef = useRef<HTMLDivElement>(null);
    const bannerRef = useRef<HTMLImageElement>(null);
    useHeroParallax(sectionRef, bannerRef, !reducedMotion);

    const onGetStarted = () => {
        handleCTAClick('startProject');
        handleLoginClick();
    };

    const renderCredit = (className: string) => (
        <RoccoLink
            to={featuredProject.href}
            className={`font-sans leading-tight font-medium opacity-60 transition-opacity hover:opacity-100 ${className}`}
        >
            <span className="block">
                Featured Project: {featuredProject.name}
            </span>
            <span className="block">by {featuredProject.architect}</span>
        </RoccoLink>
    );

    return (
        <section className="relative w-full bg-black text-white">
            {/* Desktop — banner with parallax + overlay anchored to the
                1920×1373 frame. */}
            <div
                ref={sectionRef}
                className="relative hidden overflow-hidden md:block md:h-[71.51vw]"
            >
                {/* Banner rendered 12% taller so the parallax never shows an
                    edge. */}
                <img
                    ref={bannerRef}
                    src={image}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    className="absolute inset-x-0 top-[-6%] h-[112%] w-full object-cover will-change-transform"
                />

                {/* Bottom gradient for legibility (Figma `1420:30855`):
                    #131313 fading up to transparent over the lower ~43%. */}
                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-[42.97%] bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
                />

                <div className="absolute inset-0">
                    <RevealHeadline
                        className="absolute m-0 leading-[6.77vw] font-normal tracking-[-0.3vw] text-[#e5e5e5]"
                        animate={shouldAnimateHeadline}
                        style={{
                            left: '2.97%',
                            top: '50%',
                            fontSize: '6.25vw',
                            fontFamily: HEADLINE_FONT,
                        }}
                    />

                    <div
                        className="absolute flex items-center justify-between"
                        style={{
                            left: '24.64%',
                            top: '89.51%',
                            width: '44.65%',
                        }}
                    >
                        <p
                            className="m-0 font-sans font-medium text-[#fafafa]"
                            style={{
                                width: '75.46%',
                                fontSize: '1.14vw',
                                lineHeight: '1.426vw',
                            }}
                        >
                            ArchiPro is the all-in-one platform helping people
                            find trusted products and professionals for their
                            building project
                        </p>
                        <button
                            type="button"
                            onClick={onGetStarted}
                            className="flex shrink-0 items-center justify-center rounded-full bg-[#f2f2f2] font-sans font-normal whitespace-nowrap text-[#141414] transition-colors hover:bg-white"
                            style={{
                                paddingInline: '1.71vw',
                                paddingBlock: '0.57vw',
                                fontSize: '1.14vw',
                            }}
                        >
                            Get Started
                        </button>
                    </div>

                    <div
                        className="absolute text-right"
                        style={{
                            right: '1.93%',
                            bottom: '3.06%',
                            fontSize: '0.833vw',
                        }}
                    >
                        {renderCredit('text-white')}
                    </div>
                </div>
            </div>

            {/* Mobile — full-screen banner; the architect credit moves to a
                smaller caption just below the image (BC-4482). Uses a stable
                `svh` (not `dvh`) so the banner doesn't resize/jump as the mobile
                address bar shows/hides on scroll (BC-4486). */}
            <div className="md:hidden">
                <div className="relative h-[100svh] overflow-hidden">
                    <img
                        src={image}
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        className="absolute inset-0 size-full object-cover"
                    />
                    <div
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 h-[42.97%] bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
                    />
                    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 px-6 pb-10">
                        <RevealHeadline
                            className="m-0 leading-[1.05] font-normal tracking-[-0.01em] text-[#e5e5e5] max-md:text-[9vw]"
                            animate={shouldAnimateHeadline}
                            style={{ fontFamily: HEADLINE_FONT }}
                        />
                        <p className="m-0 font-sans leading-snug font-medium text-[#fafafa] max-md:text-sm">
                            ArchiPro is the all-in-one platform helping people
                            find trusted products and professionals for their
                            building project
                        </p>
                        <button
                            type="button"
                            onClick={onGetStarted}
                            className="w-fit rounded-full bg-[#f2f2f2] px-7 py-3 font-sans font-normal whitespace-nowrap text-[#141414]"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
                {/* Credit on a white strip so it blends into the section
                    below (BC-4482). */}
                <div className="bg-white px-6 py-3 text-right text-[2.3vw]">
                    {renderCredit('text-[#1f1f21]')}
                </div>
            </div>
        </section>
    );
};
